import {
  AgmCircle,
  AgmMap,
  GoogleMapsAPIWrapper,
  MapsAPILoader,
  StreetViewControlOptions,
} from '@agm/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { LoggerService, MapService } from '../../services';
import { OrganisationsService } from '../../services';
import { ContextMenuMapDirective } from './context-menu-map.directive';
import { DirectionsMapDirective } from './directions-map.directive';
import { IMarkerPoint, MarkerPoint, PauseMarkerPoint } from './marker.point';
import { TaggedLogger } from '../../services';
import { IVehicleAddress } from '../../interfaces/task-info';

export enum ReadOnlyMode {
  BothEnable = 0,
  ReadOnly = 1,
  Pickup = 2,
  Dropoff = 3,
  BothDisable = 4,
}

export interface MapControlTask {
  id: number;
  pickupLocation: IMarkerPoint;
  dropoffLocation: IMarkerPoint;
  directions?: any;
  currentLocation?: any;
  polyline?: string;
  vehicle?: {
    registrationNumber?: string;
  };
  map?: {
    taskId?: number;
    hidden?: boolean;
    directions?: {
      color?: string;
      opacity?: number;
    };
  };
}

@Component({
  selector: 'map-control',
  styleUrls: ['./map-control.component.scss'],
  templateUrl: './map-control.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MapControl implements OnInit {
  private logger: TaggedLogger;
  public latitude: number;
  public longitude: number;

  _originIcon = {
    url: 'assets/images/pickup_map_icon.png',
    scaledSize: { width: 30, height: 30 },
  };

  _destinationIcon = {
    url: 'assets/images/dropoff_map_icon.png',
    scaledSize: { width: 30, height: 30 },
  };

  public AIcon = {
    url: 'assets/images/A_map_icon.png',
    scaledSize: { width: 30, height: 30 },
  };

  public BIcon = {
    url: 'assets/images/B_map_icon.png',
    scaledSize: { width: 30, height: 30 },
  };

  public CIcon = {
    url: 'assets/images/C_map_icon.png',
    scaledSize: { width: 30, height: 30 },
  };
  public taskTrackingIcon: string = 'assets/images/car-tracking-icon.png';
  public personIcon: string = 'assets/images/walking-icon.png';
  public pauseIcon: string = 'assets/images/pause_icon.svg';

  public iconRed = {
    url: 'assets/images/map-marker-icon-red.png',
    scaledSize: { width: 40, height: 40 },
  };
  public iconBlue = {
    url: 'assets/images/map-marker-icon-blue.png',
    scaledSize: { width: 40, height: 40 },
  };
  public iconGreen = {
    url: 'assets/images/map-marker-icon-green.png',
    scaledSize: { width: 40, height: 40 },
  };
  public iconGrey = {
    url: 'assets/images/map-marker-icon-grey.png',
    scaledSize: { width: 40, height: 40 },
  };

  public showHeadingTrack: boolean = false;
  private previous: any;

  @ViewChild(DirectionsMapDirective) vc: DirectionsMapDirective;
  @ViewChild(ContextMenuMapDirective) cm: ContextMenuMapDirective;

  @ViewChild(AgmMap) set content(agmMap: AgmMap) {
    agmMap?.mapReady.subscribe((map) => {
      this.logger.debug('Google map object ready');
      this.gmap = map;

      if (!this.autofitDone) {
        void void this.autofitObjects();
      }
    });
  }

  @ViewChildren(AgmCircle) circles: QueryList<AgmCircle>;

  gmap: any;
  autofitDone: boolean = false;

  private _height: number = 500;
  @Output() heightChange: EventEmitter<number>;

  private _styleClass = '';
  private alphaIconUrls: { [key: string]: {} } = {};
  public mapVisible: boolean = true;
  private observer: IntersectionObserver | undefined;

  @Input('panTo') set panTo(value: MarkerPoint) {
    this.latitude = value.latitude;
    this.longitude = value.longitude;
  }

  @Input('lazyLoad') lazyLoad = false;

  @Input('streetViewControlOptions') streetViewControlOptions: StreetViewControlOptions;
  @Input('streetViewControl') streetViewControl: boolean;

  @Input('overlays') overlays = [];

  @Input('height')
  set height(value: number) {
    this._height = value;
    this.heightChange.emit(value);
  }

  get height(): number {
    return this._height;
  }

  _organisationIcon: string = 'assets/images/home-supplier.png';

  @Input('organisationIcon')
  set organisationIcon(value: string) {
    this._organisationIcon = value;
  }

  get organisationIcon(): string {
    return this._organisationIcon;
  }

  private _customStyle: any = { height: '650px' };
  @Output() customStyleChange: EventEmitter<any>;

  @Input('customStyle')
  set customStyle(value: any) {
    this._customStyle = value;
    this.customStyleChange.emit(value);
  }

  get customStyle(): any {
    return this._customStyle;
  }

  @Input('alwaysAutofit') alwaysAutofit: boolean;

  @Input('styleClass')
  set styleClass(value: any) {
    this._styleClass = value;
  }

  get styleClass(): any {
    return this._styleClass;
  }

  private fitOrig: IMarkerPoint;
  private fitDest: IMarkerPoint;

  private _origin: IMarkerPoint;
  @Output() originChange: EventEmitter<IMarkerPoint>;

  @Input('origin')
  set origin(value: IMarkerPoint) {
    if (
      value &&
      this._origin &&
      value.longitude == this._origin.longitude &&
      value.latitude == this._origin.latitude
    ) {
      return;
    }

    this._origin = value;
    this.originChange.emit(value);

    const needsFit =
      value != null &&
      (this.fitOrig == null ||
        this.fitOrig.latitude != value.latitude ||
        this.fitOrig.longitude != value.longitude);

    if (needsFit) {
      void this.autofitObjects();
    }
  }

  get origin(): IMarkerPoint {
    return this._origin;
  }

  private _destination: IMarkerPoint;
  @Output() destinationChange: EventEmitter<IMarkerPoint>;

  @Input('destination')
  set destination(value: IMarkerPoint) {
    if (
      value &&
      this._destination &&
      value.longitude == this._destination.longitude &&
      value.latitude == this._destination.latitude
    ) {
      return;
    }

    this._destination = value;
    this.destinationChange.emit(value);

    const needsFit =
      value != null &&
      (this.fitDest == null ||
        this.fitDest.latitude != value.latitude ||
        this.fitDest.longitude != value.longitude);

    if (needsFit) {
      void this.autofitObjects();
    }
  }

  get destination(): IMarkerPoint {
    return this._destination;
  }

  private _readOnly: boolean;
  @Input('readOnly')
  set readOnly(value: boolean) {
    this._readOnly = value;
    if (value) this.readOnlyMode = ReadOnlyMode.ReadOnly;
  }

  get readOnly(): boolean {
    return this._readOnly;
  }

  private _readOnlyMode: ReadOnlyMode;
  @Input('readOnlyMode')
  set readOnlyMode(value: ReadOnlyMode) {
    this._readOnlyMode = value;
  }

  get readOnlyMode(): ReadOnlyMode {
    return this._readOnlyMode;
  }

  private _radiusPoints: any;
  @Output() radiusPointsChange: EventEmitter<IMarkerPoint[]>;

  @Input('originIcon')
  set originIcon(value: { url: string; scaledSize: { width: number; height: number } }) {
    this._originIcon = value;
  }

  get originIcon() {
    return this._originIcon;
  }

  @Input('destinationIcon')
  set destinationIcon(value: { url: string; scaledSize: { width: number; height: number } }) {
    this._destinationIcon = value;
  }

  get destinationIcon() {
    return this._destinationIcon;
  }

  @Input('radiusPoints')
  set radiusPoints(value: IMarkerPoint[]) {
    this._radiusPoints = value;
    this.radiusPointsChange.emit(value);
  }

  get radiusPoints(): any {
    return this._radiusPoints;
  }

  private _tasks: any;
  @Output() tasksChange: EventEmitter<MapControlTask[]>;

  @Input('tasks')
  set tasks(value: MapControlTask[]) {
    this._tasks = value;
    this.tasksChange.emit(value);

    if (!this.autofitDone || this.alwaysAutofit) {
      void this.autofitObjects();
    }

    if (value != null) {
      this.alphaIconUrls = this.mapService.setAlphaIcons(value);
    }
  }

  get tasks(): MapControlTask[] {
    return this._tasks;
  }

  private _zoom = 6;
  @Output() zoomChange: EventEmitter<number>;

  @Input('zoom')
  set zoom(value: any) {
    this._zoom = value;
    this.zoomChange.emit(value);
  }

  get zoom(): any {
    return this._zoom;
  }

  private _plannedTasks: any;
  @Output() plannedTasksChange: EventEmitter<MarkerPoint>;

  @Input('plannedTasks')
  set plannedTasks(value: any) {
    this._plannedTasks = value;
    this.plannedTasksChange.emit(value);

    if (!this.autofitDone) void this.autofitObjects();
  }

  get plannedTasks(): any {
    return this._plannedTasks;
  }

  // suggestedTasks are used in ECMatching UI
  private _suggestedTasks: any;
  @Output() suggestedTasksChange: EventEmitter<MarkerPoint>;

  @Input('suggestedTasks')
  set suggestedTasks(value: any) {
    this._suggestedTasks = value;

    this.suggestedTasksChange.emit(value);

    void this.autofitObjects();
  }

  get suggestedTasks(): any {
    return this._suggestedTasks;
  }

  private _liveTask: any;
  @Output() liveTaskChange: EventEmitter<MarkerPoint>;

  @Input('liveTask')
  set liveTask(value: any) {
    this._liveTask = value;

    if (this._liveTask) {
      this.earliestPickup = this._liveTask.pickupEarliestDateTime;
    }

    this.liveTaskChange.emit(value);
  }

  get liveTask(): any {
    return this._liveTask;
  }

  private _selectedTask: any;
  @Output() selectedTaskChange: EventEmitter<any>;

  @Input('selectedTask')
  set selectedTask(value: any) {
    this._selectedTask = value;

    if (this._selectedTask) {
      this.earliestPickup = this._selectedTask.pickupEarliestDateTime;
    }

    this.selectedTaskChange.emit(value);
  }

  get selectedTask(): any {
    return this._tasks;
  }

  private _organisations: any;
  @Output() organisationsChange: EventEmitter<MarkerPoint>;

  @Input('organisations')
  set organisations(value: any) {
    this._organisations = value;
    this.organisationsChange.emit(value);
  }

  get organisations(): any {
    return this._organisations;
  }

  private _suppliers: any;
  @Output() suppliersChange: EventEmitter<MarkerPoint>;

  @Input('suppliers')
  set suppliers(value: any) {
    this._suppliers = value;
    this.suppliersChange.emit(value);
  }

  get suppliers(): any {
    return this._suppliers;
  }

  private _pauses: PauseMarkerPoint[];

  @Input('pauses')
  set pauses(value: PauseMarkerPoint[]) {
    this._pauses = value;
  }

  get pauses(): PauseMarkerPoint[] {
    return this._pauses;
  }

  // this should be passed to the map control component when no Task is available (i.e. when creating an order)
  // so that the map control can use the pessimistic directions parameter and get consistent results
  private _earliestPickup: Date;
  @Input('earliestPickup')
  set earliestPickup(value: Date) {
    this._earliestPickup = value;
  }

  get earliestPickup() {
    return this._earliestPickup;
  }

  @Input() alphaIcons: boolean = false;

  @Output() driverPlanClick: EventEmitter<any>;
  @Output() addSupplierClick: EventEmitter<any>;
  @Output() overlayClick: EventEmitter<any>;

  @Output() mapClicked: EventEmitter<any> = new EventEmitter<any>();

  // @ViewChild('mapContainer') mapContainer: ElementRef;

  public headingToPickupLocation = 'Heading to Pick-up Location';

  constructor(
    private mapsAPILoader: MapsAPILoader,
    private ngZone: NgZone,
    private gmapsApi: GoogleMapsAPIWrapper,
    private organisationsService: OrganisationsService,
    private cd: ChangeDetectorRef,
    private mapService: MapService,
    loggerService: LoggerService,
  ) {
    this.originChange = new EventEmitter<MarkerPoint>();
    this.destinationChange = new EventEmitter<MarkerPoint>();
    this._readOnly = false;
    this.radiusPointsChange = new EventEmitter<IMarkerPoint[]>();
    this.heightChange = new EventEmitter<number>();
    this.tasksChange = new EventEmitter<MapControlTask[]>();
    this.selectedTaskChange = new EventEmitter<any>();
    this.customStyleChange = new EventEmitter<any>();
    this.liveTaskChange = new EventEmitter<MarkerPoint>();
    this.organisationsChange = new EventEmitter<MarkerPoint>();
    this.plannedTasksChange = new EventEmitter<MarkerPoint>();
    this.suggestedTasksChange = new EventEmitter<MarkerPoint>();
    this.suppliersChange = new EventEmitter<MarkerPoint>();
    this.zoomChange = new EventEmitter<number>();
    this.driverPlanClick = new EventEmitter<any>();
    this.addSupplierClick = new EventEmitter<any>();
    this.overlayClick = new EventEmitter<any>();
    this.logger = loggerService.taggedLogger('MapControl');
  }

  ngOnInit() {
    this.latitude = 52.5;
    this.longitude = -2.0;

    const that = this;

    if (this.lazyLoad && this.mapVisible == false) {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.mapVisible = true;
            this.cd.markForCheck();
            this.observer.disconnect();
          }
        });
      });
    } else {
      this.mapVisible = true;
    }

    this.mapsAPILoader.load().then(() => {
      if (that.cm) {
        that.cm.Initialize();
      }
    });

    this.organisationsService.getCurrentUserOrgTypeId().then((res) => {
      //show if not customer
      this.showHeadingTrack = res != 2;
    });
  }

  async autofitObjects() {
    let callAutoFit: boolean = true;

    if (this.gmap) {
      let autoFitTo: google.maps.LatLng[] = [];

      if (this._tasks) {
        this._tasks.forEach((o) => {
          autoFitTo.push(
            new google.maps.LatLng(o.pickupLocation.latitude, o.pickupLocation.longitude),
          );
          autoFitTo.push(
            new google.maps.LatLng(o.dropoffLocation.latitude, o.dropoffLocation.longitude),
          );

          if (o.currentLocation)
            autoFitTo.push(
              new google.maps.LatLng(o.currentLocation.latitude, o.currentLocation.longitude),
            );
        });
      }
      if (this._plannedTasks) {
        this._plannedTasks.forEach((o) => {
          autoFitTo.push(
            new google.maps.LatLng(o.pickupLocation.latitude, o.pickupLocation.longitude),
          );
          autoFitTo.push(
            new google.maps.LatLng(o.dropoffLocation.latitude, o.dropoffLocation.longitude),
          );
        });
      }
      if (this._suggestedTasks) {
        this._suggestedTasks.forEach((o) => {
          autoFitTo.push(
            new google.maps.LatLng(o.pickupLocation.latitude, o.pickupLocation.longitude),
          );
          autoFitTo.push(
            new google.maps.LatLng(o.dropoffLocation.latitude, o.dropoffLocation.longitude),
          );
        });
      }
      if (this.circles.length > 0) {
        const circleArray = this.circles.toArray();
        for (let i = 0; i < circleArray.length; ++i) {
          const bound = await circleArray[i].getBounds();
          autoFitTo.push(
            new google.maps.LatLng(bound.getNorthEast().lat(), bound.getNorthEast().lng()),
          );
          autoFitTo.push(
            new google.maps.LatLng(bound.getSouthWest().lat(), bound.getSouthWest().lng()),
          );
        }
      }

      if (
        this._origin &&
        this._origin.latitude &&
        this._origin.longitude &&
        this._destination &&
        this._destination.latitude &&
        this._destination.longitude
      ) {
        // todo: double check that overwriting the var here is expected, it was what was going on previously.
        //autofit by orig & dest objects
        autoFitTo = [
          new google.maps.LatLng(this._origin.latitude, this._origin.longitude),
          new google.maps.LatLng(this._destination.latitude, this._destination.longitude),
        ];

        this.fitOrig = this._origin;
        this.fitDest = this._destination;
      }

      // If we only have a single point
      if (
        !this._suggestedTasks &&
        !this._tasks &&
        !this._plannedTasks &&
        !this.circles.length &&
        !this._destination &&
        this._origin &&
        this._origin.latitude != null &&
        this._origin.longitude != null
      ) {
        // We only have a single point to plot
        this.gmap.setCenter(new google.maps.LatLng(this._origin.latitude, this._origin.longitude));
        callAutoFit = false;
      }

      if (callAutoFit) {
        this.autofit(autoFitTo);
      }
    }
  }

  autofit(latLngList: google.maps.LatLng[]) {
    if (this.gmap && latLngList && latLngList.length) {
      const bounds = new google.maps.LatLngBounds();
      let i = 0;
      const LtLgLen = latLngList.length;
      for (; i < LtLgLen; i++) bounds.extend(latLngList[i]);

      this.gmap.fitBounds(bounds);
      this.autofitDone = true;
    }
  }

  private SetOrigin(lng: number, lat: number) {
    let selectedOrigin = new MarkerPoint();

    selectedOrigin.longitude = lng;
    selectedOrigin.latitude = lat;

    this.originChange.emit(selectedOrigin);
  }

  SetStart() {
    this.ngZone.run(() => {
      this.SetOrigin(this.cm.lng, this.cm.lat);
      this.cm.HideContexMenu();
    });
  }

  private SetDestination(lng: number, lat: number) {
    let selectedDestination = new MarkerPoint();

    selectedDestination.longitude = lng;
    selectedDestination.latitude = lat;

    this.destinationChange.emit(selectedDestination);
  }

  SetEnd() {
    this.ngZone.run(() => {
      this.SetDestination(this.cm.lng, this.cm.lat);
      this.cm.HideContexMenu();
    });
  }

  originMarkerDragEnd(mouseEvent) {
    this.ngZone.run(() => {
      this.SetOrigin(mouseEvent.coords.lng, mouseEvent.coords.lat);
    });
  }

  destinationMarkerDragEnd(mouseEvent) {
    this.ngZone.run(() => {
      this.SetDestination(mouseEvent.coords.lng, mouseEvent.coords.lat);
    });
  }

  taskClick(taskData: any) {
    this.selectedTask = taskData;
  }

  filterVehicleTrack(track, isHeading) {
    return track.filter((o) => o.isHeading == isHeading);
  }

  clickedMarker(infowindow) {
    if (this.previous) {
      this.previous.close();
    }
    this.previous = infowindow;
  }

  driverPlan(supplier) {
    this.driverPlanClick.emit(supplier);
  }

  getIconURL(supplier) {
    if (supplier.taskCount > 0) {
      if (supplier.isJobDistance) {
        return this.iconBlue;
      }
      return this.iconRed;
    } else {
      if (supplier.isAvailable) return this.iconGreen;
      else return this.iconGrey;
    }
  }

  getDestinationIcon(tasks, task: MapControlTask) {
    if (this.alphaIcons) {
      return this.getAlphaIcon(tasks, task, false);
    } else {
      return this.destinationIcon;
    }
  }

  getOriginIcon(tasks, task: MapControlTask) {
    if (this.alphaIcons) {
      return this.getAlphaIcon(tasks, task, true);
    } else {
      return this.originIcon;
    }
  }

  getAlphaIcon(tasks, task, isPickup: boolean) {
    return this.alphaIconUrls[task.id][isPickup ? 'pickup' : 'dropoff'];
  }

  visibleTasksNoPolyline(tasks: MapControlTask[]) {
    if (!tasks) return [];

    return tasks.filter((o) => !o.map?.hidden && !o.polyline);
  }

  visibleTasksWithPolyline(tasks: MapControlTask[]) {
    if (!tasks) return [];

    return tasks.filter((o) => !o.map?.hidden && o.polyline);
  }

  haveClicked($event) {
    this.overlayClick.emit($event);
  }

  onMapClicked(event) {
    this.mapClicked.emit({ lat: event.coords.lat, lng: event.coords.lng });
  }

  // onMapReady($event: any) {
  //   this.initMap();
  // }
}
